class ScrollScatter extends HTMLElement {
  connectedCallback() {
    this.init();
  }

  init() {
    this.items = Array.from(this.querySelectorAll("[data-parallax-cards-item]"));
    this.lead = this.querySelector("[data-parallax-cards-lead]");
    this.cta = this.querySelector("[data-parallax-cards-cta]");
    this.glow = this.querySelector("[data-parallax-cards-glow]");
    this.reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.animationFrame = null;

    if (!this.items.length) return;

    if (this.reduceMotion) {
      this.renderReducedMotion();
      return;
    }

    this.render = this.render.bind(this);
    this.render(0);
  }

  disconnectedCallback() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
  }

  clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  lerp(start, end, progress) {
    return start + (end - start) * progress;
  }

  ease(progress) {
    if (progress < 0.5) {
      return 4 * progress * progress * progress;
    }

    return 1 - Math.pow(-2 * progress + 2, 3) / 2;
  }

  progress() {
    const total = this.offsetHeight - window.innerHeight;

    if (total <= 0) return 1;

    return this.clamp(-this.getBoundingClientRect().top / total, 0, 1);
  }

  render(time = 0) {
    const progress = this.progress();
    const easedProgress = this.ease(progress);
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const isMobile = viewportWidth <= 767.98;
    const endOverflow = isMobile ? 1.20 : 1;
    const totalItems = this.items.length;

    // Get animation style from attribute
    const animStyle = this.getAttribute("data-animation-style") || "scatter";

    this.items.forEach((item, index) => {
      const data = item.dataset;
      
      // Parse coordinates with safe fallbacks
      const sxVal = Number(data.sx) || 0;
      const syVal = Number(data.sy) || 0;
      const srVal = Number(data.sr) || 0;
      const ssVal = Number(data.ss) || 0.58;
      
      const fxVal = Number(data.fx) || 0;
      const fyVal = Number(data.fy) || 0;
      const frVal = Number(data.fr) || 0;
      const fsVal = Number(data.fs) || 0.95;

      let x = 0;
      let y = 0;
      let rotation = 0;
      let scale = 1;
      let opacity = 1;

      if (animStyle === "scatter") {
        // 1. SCATTER OUTWARDS (Original effect)
        const startX = sxVal * Math.min(viewportWidth, item.offsetWidth * 4.2);
        const endX = fxVal * viewportWidth * endOverflow;
        x = this.lerp(startX, endX, easedProgress);
        y = this.lerp(syVal, fyVal * endOverflow, easedProgress) * viewportHeight;
        rotation = this.lerp(srVal, frVal, easedProgress);
        const endScale = fsVal * (viewportWidth < 576 ? 0.78 : 1);
        scale = this.lerp(ssVal, endScale, easedProgress);
      } 
      else if (animStyle === "converge") {
        // 2. CONVERGE INWARDS (Reverse of scatter)
        const startX = fxVal * viewportWidth * endOverflow;
        const endX = sxVal * Math.min(viewportWidth, item.offsetWidth * 1.5);
        x = this.lerp(startX, endX, easedProgress);
        y = this.lerp(fyVal * endOverflow, syVal, easedProgress) * viewportHeight;
        rotation = this.lerp(frVal, srVal, easedProgress);
        const startScale = fsVal * (viewportWidth < 576 ? 0.78 : 1);
        scale = this.lerp(startScale, ssVal, easedProgress);
      } 
      else if (animStyle === "fan") {
        // 3. FAN OUT (Spread in a semi-circular arc)
        const maxAngle = isMobile ? 35 : 55; // Spread angle in degrees
        const angle = -maxAngle/2 + (index / (totalItems - 1 || 1)) * maxAngle;
        const rad = (angle * Math.PI) / 180;
        
        const maxRadius = isMobile ? viewportWidth * 0.32 : viewportWidth * 0.25;
        const radius = this.lerp(0, maxRadius, easedProgress);
        
        x = Math.sin(rad) * radius;
        y = -Math.cos(rad) * radius + (isMobile ? viewportHeight * 0.08 : viewportHeight * 0.12);
        rotation = this.lerp(0, angle, easedProgress);
        
        const startScale = 0.55;
        const endScale = 0.92 * (isMobile ? 0.75 : 1);
        scale = this.lerp(startScale, endScale, easedProgress);
      } 
      else if (animStyle === "stack") {
        // 4. STICKY PILE (Postcard deck style, enters sequentially from bottom)
        const cardRotations = [-5, 4, -2, 5, -1];
        const startProgress = (index / totalItems) * 0.6;
        const endProgress = startProgress + 0.4;
        
        const cardProgress = this.clamp((progress - startProgress) / (endProgress - startProgress), 0, 1);
        const easedCardProgress = this.ease(cardProgress);
        
        x = this.lerp(0, (index - (totalItems - 1) / 2) * (isMobile ? 6 : 12), easedCardProgress);
        y = this.lerp(viewportHeight * 0.8, (index - (totalItems - 1) / 2) * (isMobile ? -2 : -4), easedCardProgress);
        rotation = this.lerp(12, cardRotations[index % 5], easedCardProgress);
        
        const startScale = 0.45;
        const endScale = (0.95 - (totalItems - 1 - index) * 0.03) * (isMobile ? 0.78 : 1);
        scale = this.lerp(startScale, endScale, easedCardProgress);
        opacity = easedCardProgress;
      } 
      else if (animStyle === "zoom_tunnel") {
        // 5. 3D TUNNEL ZOOM (Images emerge tiny and fly past the screen)
        const startProgress = (index / totalItems) * 0.5;
        const duration = 0.5;
        const cardProgress = this.clamp((progress - startProgress) / duration, 0, 1);
        const easedCardProgress = this.ease(cardProgress);
        
        // Distribute items in radial angles
        const angle = (index / (totalItems - 1 || 1)) * 2 * Math.PI;
        const spreadDist = isMobile ? viewportWidth * 0.42 : viewportWidth * 0.55;
        
        x = Math.cos(angle) * spreadDist * easedCardProgress;
        y = Math.sin(angle) * spreadDist * easedCardProgress;
        rotation = srVal + (frVal - srVal) * easedCardProgress;
        
        const startScale = 0.1;
        const endScale = 1.35 * (isMobile ? 0.75 : 1);
        scale = this.lerp(startScale, endScale, easedCardProgress);
        
        // Smooth fade-in and quick fade-out at limits
        if (easedCardProgress < 0.25) {
          opacity = easedCardProgress / 0.25;
        } else if (easedCardProgress > 0.8) {
          opacity = this.clamp(1 - (easedCardProgress - 0.8) / 0.2, 0, 1);
        } else {
          opacity = 1;
        }
      }

      item.style.transform = `translate(-50%, -50%) translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) rotate(${rotation.toFixed(2)}deg) scale(${scale.toFixed(3)})`;
      item.style.opacity = opacity.toFixed(3);
    });

    this.renderContent(progress, viewportWidth);
    this.animationFrame = requestAnimationFrame(this.render);
  }

  renderContent(progress, viewportWidth) {
    const head = this.querySelector(".parallax-cards__head");
    const veil = this.querySelector(".parallax-cards__veil");

    if (head) {
      const headProgress = this.clamp(progress / 0.22, 0, 1);
      const headOffset = this.clamp(viewportWidth * 0.08, 32, 100);
      head.style.transform = `translateY(${this.lerp(-headOffset, 0, headProgress).toFixed(1)}px)`;
    }

    if (veil) {
      const veilProgress = this.clamp((progress - 0.08) / 0.24, 0, 1);
      veil.style.opacity = veilProgress;
      veil.style.transform = `scale(${this.lerp(0.96, 1, veilProgress).toFixed(3)})`;
    }

    if (this.lead) {
      const leadProgress = this.clamp((progress - 0.42) / 0.22, 0, 1);
      this.lead.style.opacity = leadProgress;
      this.lead.style.transform = `translateY(${this.lerp(14, 0, leadProgress).toFixed(1)}px)`;
    }

    if (this.cta) {
      const ctaProgress = this.clamp((progress - 0.56) / 0.22, 0, 1);
      this.cta.style.opacity = ctaProgress;
      this.cta.style.transform = `translateY(${this.lerp(14, 0, ctaProgress).toFixed(1)}px)`;
    }

    if (this.glow) {
      this.glow.style.opacity = (0.4 + this.ease(progress) * 0.4).toFixed(2);
      this.glow.style.transform = `translate(-50%, -50%) scale(${(1 + this.ease(progress) * 0.5).toFixed(3)})`;
    }
  }

  renderReducedMotion() {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const isMobile = viewportWidth <= 767.98;
    const endOverflow = isMobile ? 1.20 : 1;

    this.items.forEach((item) => {
      const data = item.dataset;
      const fsVal = Number(data.fs) || 0.95;
      const fxVal = Number(data.fx) || 0;
      const fyVal = Number(data.fy) || 0;
      
      const scale = fsVal * (isMobile ? 0.78 : 1);
      item.style.transform = `translate(-50%, -50%) translate(${fxVal * viewportWidth * endOverflow}px, ${fyVal * viewportHeight * endOverflow}px) scale(${scale})`;
      item.style.opacity = 1;
    });

    if (this.lead) this.lead.style.opacity = 1;
    if (this.cta) this.cta.style.opacity = 1;
  }
}

if (!customElements.get("scroll-scatter")) {
  customElements.define("scroll-scatter", ScrollScatter);
}